import type { ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useDocumentHead } from '@/lib/seo/useDocumentHead';
import { ButtonLink, Container, Section, SectionHeading } from '@/components/ui';

/**
 * The 404 page.
 *
 * In production the SPA host has already returned a real 404 status alongside this
 * shell, so a crawler sees the correct code rather than the soft-404 a client-rendered
 * app usually produces. This component supplies the human half.
 */
export function NotFoundPage(): ReactNode {
  const location = useLocation();

  useDocumentHead({
    title: 'Page not found',
    canonicalPath: location.pathname,
    noIndex: true,
  });

  return (
    <Section className="pt-32 md:pt-40">
      <Container className="flex max-w-2xl flex-col items-center gap-8 text-center">
        <p className="text-[5rem] font-semibold leading-none text-outline">404</p>
        <SectionHeading
          as="h1"
          title="We could not find that page"
          description="The link may be out of date, or the page may have been taken down. Everything else is still where you left it."
        />
        <div className="flex flex-wrap justify-center gap-3">
          <ButtonLink to="/">Back to home</ButtonLink>
          <ButtonLink to="/blog" variant="secondary">
            Read our insights
          </ButtonLink>
        </div>
      </Container>
    </Section>
  );
}
